import React, { useCallback, useState } from 'react';
import { PortfolioBuyerTable } from './data';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import { PortfoilioLead } from 'api/portfolio/buyer';
import { useTranslation } from 'react-i18next';
import { NudgeNotification } from 'models/Notification';
import { WithdrawButton } from '../../../../../../components/BuyerActions/WithdrawButton';
import { BuyerNudgeNotification } from '../../../../../../components/BuyerNudgeNotification';
import { CellProps, COMMON_BODY_CONTENT_GENERATE } from '../../../TableCells';

export type BuyerCellPropsa = CellProps & {
  _id?: string;
  lastSeen?: Date;
  nudgeNotification?: NudgeNotification;
  config?: {
    buyerStep?: LeadStages;
    withdrawnInterest?: boolean;
    confirmedInterest?: boolean;
    leadId?: string;
    dealId?: string;
    dealName?: string;
    onSubmitSuccess?: () => void;
    updateLead?: (lead: Partial<PortfoilioLead>) => void;
  };
};

const WithdrawCellComponent: React.FC<BuyerCellPropsa> = ({
  config: {
    withdrawnInterest,
    buyerStep,
    confirmedInterest,
    leadId,
    dealId,
    dealName,
    onSubmitSuccess,
  },
}) => {
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const { t } = useTranslation();

  const canWithdrawn =
    !withdrawnInterest &&
    (buyerStep !== LEAD_STAGES.ACCEPT || !confirmedInterest);

  if (!canWithdrawn) return null;

  return (
    <WithdrawButton
      className="pdt-withdraw-cell"
      leadId={leadId}
      dealId={dealId}
      dealName={dealName}
      isLoading={isWithdrawing}
      withdrawText={t('withdraw')}
      setIsLoading={setIsWithdrawing}
      onSuccess={onSubmitSuccess}
    />
  );
};

export const NudgeCellComponent: React.FC<BuyerCellPropsa> = ({
  _id,
  nudgeNotification,
  config,
}) => {
  const { onSubmitSuccess, updateLead } = config;

  const onSubmitNudgeNotification = useCallback(() => {
    updateLead?.({ _id, nudgeNotification: null });
    onSubmitSuccess?.();
  }, [_id, onSubmitSuccess, updateLead]);

  const onWithdraw = useCallback(() => {
    updateLead?.({
      _id,
      nudgeNotification: null,
      withdrawnInterest: true,
    });
    onSubmitSuccess?.();
  }, [_id, onSubmitSuccess, updateLead]);

  return (
    <BuyerNudgeNotification
      className="pdt-nudge-cell"
      nudge={nudgeNotification}
      onConfirmInterest={onSubmitNudgeNotification}
      onSnooze={onSubmitNudgeNotification}
      onWithdraw={onWithdraw}
    />
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof PortfolioBuyerTable,
  (cellProps: BuyerCellPropsa) => React.ReactNode
> = {
  ...COMMON_BODY_CONTENT_GENERATE,
  withdrawInterest: (cellProps: CellProps) => (
    <WithdrawCellComponent {...cellProps} />
  ),
};
